import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * One outgoing message or answer per copilot turn.
 *
 * `isStreaming` and `currentQuestion` are React state, so several Send clicks
 * (or Enter presses) landing before the next render all read the pre-send
 * values and each start a turn: three bubbles, three messages, one server turn.
 * The ref is set synchronously by the first send and released when the turn
 * hands control back (turn end, idle, error, a new question) or, as a backstop,
 * on any render where nothing is streaming any more.
 */
export default function useTurnSendGuard(isStreaming) {
  const heldRef = useRef(false);

  const claim = useCallback(() => {
    if (heldRef.current) return false;
    heldRef.current = true;
    return true;
  }, []);

  const release = useCallback(() => {
    heldRef.current = false;
  }, []);

  // A send sets isStreaming in the same batch as the claim, so the first render
  // after a claim always shows streaming; a render without it means the turn is over.
  useEffect(() => {
    if (!isStreaming) heldRef.current = false;
  });

  return useMemo(() => ({ claim, release }), [claim, release]);
}
